/**
 * HTTP-клиент трекера: локаль, токен и обновление протухшей сессии.
 *
 * Refresh идёт через отдельный «голый» axios без интерцепторов — иначе его
 * собственный 401 снова попал бы сюда и получился бы цикл refresh → 401 →
 * refresh. На этом уже обжигались в CRM.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { clearTokens, readTokens, writeTokens } from '@/features/auth/tokens'
import type { LoginResponse } from '@/features/auth/types'
import { currentLocale } from '@/i18n'

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    /** Запрос уже повторяли после обновления токена */
    _retry?: boolean
  }
}

const REFRESH_PATH = '/api/v1/auth/refresh'
const LOGIN_PATH = '/api/v1/auth/login'

const baseURL = import.meta.env.VITE_API_BASE_URL || ''

export const http = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

/** Совпадение пути без query — чтобы `/auth/login-history` не считался логином. */
const isPath = (url: string | undefined, path: string): boolean => {
  const clean = (url ?? '').split('?')[0] ?? ''

  return clean === path || clean.endsWith(path)
}

const refreshClient = axios.create({ baseURL })

// Один общий refresh «в полёте»: на старте экран шлёт несколько запросов
// сразу, и каждый их 401 иначе завёл бы свой запрос обновления.
let refreshPromise: Promise<string> | null = null
let isRedirecting = false

const redirectToLogin = () => {
  clearTokens()

  if (isRedirecting) return
  isRedirecting = true

  // Полная перезагрузка сбрасывает состояние мёртвой сессии целиком —
  // дешевле, чем вычищать сторы по одному.
  const redirect = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.href = `/login?redirect=${redirect}`
}

const refreshTokens = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const { refreshToken } = readTokens()

      if (!refreshToken) throw new Error('No refresh token available')

      // Токен уходит заголовком: тело этой ручки бэкенд не читает.
      const { data } = await refreshClient.post<LoginResponse>(REFRESH_PATH, null, {
        headers: { 'Refresh-Token': `Bearer ${refreshToken}` },
      })

      const tokens = data?.tokens

      if (!tokens?.accessToken || !tokens.refreshToken) {
        throw new Error('Malformed refresh response')
      }

      writeTokens(tokens)

      // Стор поднимается позже клиента, поэтому обновляем его динамически и
      // только если он уже есть — источник истины всё равно localStorage.
      void import('@/features/auth/store')
        .then(({ useAuthStore }) => useAuthStore().setTokens(tokens))
        .catch(() => {})

      return tokens.accessToken
    })().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers.set('user-locale', currentLocale())

  const { accessToken } = readTokens()

  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`)
  }

  return config
})

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config

    if (error.response?.status !== 401 || !request) {
      return Promise.reject(error)
    }

    // 401 на логине — это неверные учётные данные, их показывает форма.
    if (isPath(request.url, LOGIN_PATH)) {
      return Promise.reject(error)
    }

    // 401 самого refresh означает, что сессия мертва.
    if (isPath(request.url, REFRESH_PATH)) {
      redirectToLogin()

      return Promise.reject(error)
    }

    // Повтор после успешного обновления снова дал 401 — причина не в токене,
    // выкидывать из сессии не за что.
    if (request._retry) {
      return Promise.reject(error)
    }

    request._retry = true

    try {
      const accessToken = await refreshTokens()
      request.headers.set('Authorization', `Bearer ${accessToken}`)

      return await http(request)
    } catch (refreshError) {
      redirectToLogin()

      return Promise.reject(refreshError)
    }
  },
)
